import type { DateRangeValue } from '@ui';

/**
 * "Current month" / "Last month" should read as the actual month (e.g.
 * "July"), not a static label — and without the year, per design. Ranges
 * spanning more than one month keep their "Last N months" wording.
 *
 * Use `to` (not `from`) to derive the month name: DateRangeFilter clamps
 * `from` forward when it precedes the selected org's creation month (e.g.
 * an org created this month has its "last month" `from` pushed to the 1st
 * of *this* month), but `to` is never clamped, so it always reflects the
 * true calendar month for these single-month presets.
 */
export function getDateLabel(dateRange: DateRangeValue | null): string {
  if (!dateRange || dateRange.preset === 'current_month') {
    const monthDate = dateRange?.to ?? new Date();
    return monthDate.toLocaleDateString('en-US', { month: 'long' });
  }
  switch (dateRange.preset) {
    case 'last_month':
      return dateRange.to.toLocaleDateString('en-US', { month: 'long' });
    case 'last_two_months':
      return 'Last 2 months';
    case 'last_three_months':
      return 'Last 3 months';
    case 'month':
      // The "Month…" picker itself always keeps the year in its own label
      // (e.g. "June 2026") — but footers should drop the year when the
      // picked month falls in the current year.
      if (dateRange.to.getFullYear() === new Date().getFullYear()) {
        return dateRange.to.toLocaleDateString('en-US', { month: 'long' });
      }
      return dateRange.label;
    default:
      return dateRange.label;
  }
}

/** True when the selected range includes the current (still-accumulating) month. */
export function isOngoingPeriod(dateRange: DateRangeValue | null): boolean {
  if (!dateRange || dateRange.preset === 'current_month') return true;
  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return dateRange.to >= currentMonthStart;
}

/**
 * True when `createdAt` falls on or before the end of the selected period —
 * used to scope the cumulative "Total Users" count to the selected date
 * range (e.g. picking a past month should count only users provisioned by
 * that month's end, not every user provisioned since). With no range
 * selected, every user counts.
 *
 * Compares at day granularity (end-of-day on `dateRange.to`) rather than the
 * raw Date, since single-month presets set `to` to midnight of the last day
 * of the month — a straight `<=` on that would wrongly exclude users created
 * later that same calendar day.
 */
export function isProvisionedByPeriodEnd(
  createdAt: string,
  dateRange: DateRangeValue | null
): boolean {
  if (!dateRange) return true;
  const cutoff = new Date(
    dateRange.to.getFullYear(),
    dateRange.to.getMonth(),
    dateRange.to.getDate(),
    23,
    59,
    59,
    999
  );
  return new Date(createdAt).getTime() <= cutoff.getTime();
}
